/**
 * QueryCacheAuthBoundary — clears the React Query cache on a user-identity
 * change to prevent a cross-user data leak (see useResetQueryCacheOnUserChange).
 *
 * Mount this INSIDE the QueryClientProvider and inside the AuthProvider (so both
 * `useQueryClient()` and `useAuth()` resolve). It gates descendants until the
 * settled identity has synchronized payment ownership and cleared any previous
 * identity's query cache. The gate resolves in a layout effect, before paint.
 *
 *   <AuthProvider>
 *     <QueryClientProvider client={client}>
 *       <QueryCacheAuthBoundary>
 *         <App />
 *       </QueryCacheAuthBoundary>
 *     </QueryClientProvider>
 *   </AuthProvider>
 */
import type { ReactNode } from "react";
import { useResetQueryCacheOnUserChange } from "../hooks";

export function QueryCacheAuthBoundary({ children }: { children: ReactNode }) {
  const ready = useResetQueryCacheOnUserChange();
  return ready ? <>{children}</> : null;
}
